import { extractTenantAnalyticsCampaignParameters } from '../sanitize';
import type {
  TenantAnalyticsAdapter,
  TenantAnalyticsCampaignParameters,
  TenantAnalyticsEnvelope,
  TenantAnalyticsItem,
  TenantBeginCheckoutEvent,
  TenantPageViewEvent,
  TenantPurchaseEvent,
  TenantViewContentEvent,
} from '../types';
import {
  type GoogleTagManagerPublicConfig,
  parseGoogleTagManagerPublicConfig,
} from './config';

interface GoogleTagManagerEventBase {
  readonly event: 'page_view' | 'view_content' | 'begin_checkout' | 'purchase';
  readonly event_id: string;
  readonly occurred_at: string;
}

export interface GoogleTagManagerPageViewEvent
  extends GoogleTagManagerEventBase, TenantAnalyticsCampaignParameters {
  readonly event: 'page_view';
  readonly page_path: string;
  readonly page_title?: string;
  readonly page_referrer?: string;
}

export interface GoogleTagManagerViewContentEvent extends GoogleTagManagerEventBase {
  readonly event: 'view_content';
  readonly content_id: string;
  readonly content_type: string;
  readonly content_name?: string;
  readonly value?: number;
  readonly currency?: string;
}

export interface GoogleTagManagerBeginCheckoutEvent extends GoogleTagManagerEventBase {
  readonly event: 'begin_checkout';
  readonly content_id: string;
  readonly content_type: string;
  readonly content_name?: string;
  readonly value?: number;
  readonly currency?: string;
}

/** GA4-shaped ecommerce item so a container GA4 tag can map items directly. */
export interface GoogleTagManagerEcommerceItem {
  readonly item_id: string;
  readonly item_name?: string;
  readonly item_category: string;
  readonly quantity: number;
  readonly price: number;
}

export interface GoogleTagManagerPurchaseEvent extends GoogleTagManagerEventBase {
  readonly event: 'purchase';
  readonly ecommerce: {
    readonly transaction_id: string;
    readonly value: number;
    readonly currency: string;
    readonly items: readonly GoogleTagManagerEcommerceItem[];
  };
}

export type GoogleTagManagerDataLayerEvent =
  | GoogleTagManagerPageViewEvent
  | GoogleTagManagerViewContentEvent
  | GoogleTagManagerBeginCheckoutEvent
  | GoogleTagManagerPurchaseEvent;

export type GoogleTagManagerEventSink = (
  event: GoogleTagManagerDataLayerEvent,
) => void | Promise<void>;

export interface CreateGoogleTagManagerAdapterOptions {
  readonly publicConfig: unknown;
  readonly push: GoogleTagManagerEventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createGoogleTagManagerAdapter(
  options: CreateGoogleTagManagerAdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);

  if (typeof options.push !== 'function') {
    throw new Error('Google Tag Manager requires an event sink.');
  }

  const push = options.push;

  return Object.freeze({
    id: options.id ?? `google_tag_manager:${publicConfig.containerId}`,
    provider: 'google_tag_manager',
    // page_view/view_content are measurement events, so analytics consent is
    // what gates delivery. Advertising consent is expressed to the container
    // through Consent Mode (ad_storage etc.), not by withholding events —
    // otherwise an analytics-only grant would silently measure nothing.
    requiredConsent: Object.freeze(['analytics'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      const event = mapGoogleTagManagerEvent(envelope);

      if (event) {
        await push(event);
      }
    },
  });
}

function mapGoogleTagManagerEvent(
  envelope: TenantAnalyticsEnvelope,
): GoogleTagManagerDataLayerEvent | null {
  switch (envelope.event.name) {
    case 'page_view':
      return mapPageView(envelope.event);
    case 'view_content':
      return mapViewContent(envelope.event);
    case 'begin_checkout':
      return mapBeginCheckout(envelope.event);
    case 'purchase':
      return mapPurchase(envelope.event);
  }
}

function mapPageView(
  event: TenantPageViewEvent,
): GoogleTagManagerPageViewEvent {
  const campaign = extractTenantAnalyticsCampaignParameters(event.path);

  return Object.freeze({
    event: 'page_view',
    event_id: event.eventId,
    occurred_at: event.occurredAt,
    page_path: event.path,
    ...(event.title ? { page_title: event.title } : {}),
    ...(event.referrer ? { page_referrer: event.referrer } : {}),
    ...(campaign ?? {}),
  });
}

function mapViewContent(
  event: TenantViewContentEvent,
): GoogleTagManagerViewContentEvent {
  return Object.freeze({
    event: 'view_content',
    event_id: event.eventId,
    occurred_at: event.occurredAt,
    content_id: event.contentId,
    content_type: event.contentType,
    ...(event.contentName ? { content_name: event.contentName } : {}),
    ...(event.value !== undefined
      ? { value: event.value, currency: event.currency }
      : {}),
  });
}

function mapBeginCheckout(
  event: TenantBeginCheckoutEvent,
): GoogleTagManagerBeginCheckoutEvent {
  return Object.freeze({
    event: 'begin_checkout',
    event_id: event.eventId,
    occurred_at: event.occurredAt,
    content_id: event.contentId,
    content_type: event.contentType,
    ...(event.contentName ? { content_name: event.contentName } : {}),
    ...(event.value !== undefined
      ? { value: event.value, currency: event.currency }
      : {}),
  });
}

function mapPurchase(
  event: TenantPurchaseEvent,
): GoogleTagManagerPurchaseEvent {
  return Object.freeze({
    event: 'purchase',
    event_id: event.eventId,
    occurred_at: event.occurredAt,
    // GA4 deduplicates purchase reporting on transaction_id, so the container's
    // GA4 tag can safely receive replays of the same verified purchase.
    ecommerce: Object.freeze({
      transaction_id: event.transactionId,
      value: event.value,
      currency: event.currency,
      items: Object.freeze(event.items.map((item) => mapPurchaseItem(item))),
    }),
  });
}

function mapPurchaseItem(item: TenantAnalyticsItem) {
  return Object.freeze({
    item_id: item.itemId,
    ...(item.itemName ? { item_name: item.itemName } : {}),
    item_category: item.category ?? 'purchase',
    quantity: item.quantity,
    price: item.price,
  });
}

function requirePublicConfig(value: unknown): GoogleTagManagerPublicConfig {
  const publicConfig = parseGoogleTagManagerPublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid Google Tag Manager public configuration.');
  }

  return publicConfig;
}
